import { computed, type Ref } from 'vue';

interface DateFilters {
  fromDate: string;
  toDate: string;
}

// The longest range a screen accepts: `days` counts both ends, `label` is how the
// error names it ("3 meses").
export interface MaxPeriod {
  days: number;
  label: string;
}

const MS_PER_DAY = 86_400_000;

// Whole days from one local midnight to another; rounding absorbs the hour a
// daylight-saving change adds or removes in between.
function daysBetween(fromDate: Date, toDate: Date): number {
  return Math.round((toDate.getTime() - fromDate.getTime()) / MS_PER_DAY);
}

export function useDateRangeValidation(
  filters: Ref<DateFilters>,
  multiYearRangeEnabled: boolean = false,
  maxPeriod?: MaxPeriod,
) {
  const formattedFromDate = computed(() => new Date(filters.value.fromDate.replace(/-/g, '/')));
  const formattedToDate = computed(() => new Date(filters.value.toDate.replace(/-/g, '/')));

  const hasBothDates = computed(() => Boolean(filters.value.fromDate && filters.value.toDate));
  // Not "!runsBackwards": a date that does not parse compares false both ways.
  const inOrder = computed(() => formattedFromDate.value <= formattedToDate.value);
  const runsBackwards = computed(() => formattedFromDate.value > formattedToDate.value);
  const crossesYears = computed(() => !multiYearRangeEnabled &&
    formattedFromDate.value.getFullYear() !== formattedToDate.value.getFullYear());
  const exceedsMaxPeriod = computed(() => Boolean(maxPeriod) &&
    daysBetween(formattedFromDate.value, formattedToDate.value) >= (maxPeriod?.days ?? 0));

  const isValidDateRange = computed(() => hasBothDates.value &&
    inOrder.value && !crossesYears.value && !exceedsMaxPeriod.value);

  const dateRangeError = computed(() => {
    if (!hasBothDates.value) return '';
    if (runsBackwards.value) return 'La fecha inicial debe ser menor o igual a la fecha final.';
    if (crossesYears.value) return 'Las fechas deben ser del mismo año.';
    if (exceedsMaxPeriod.value) return `El rango no puede superar los ${maxPeriod?.label}.`;

    return '';
  });

  return {
    isValidDateRange,
    dateRangeError,
  };
}
